import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Toast, ToastTrigger } from './toast.model';

/**
 * ToastService
 * ------------
 * Global toast notification manager.
 *
 * Public API:
 * - trigger({...})           -> show a toast (dedup + delay)
 * - requestDismiss(id)       -> dismiss with exit animation
 * - pause(id) / resume(id)   -> hover pause/resume (timer only, no re-render)
 *
 * UX rules implemented:
 * - 300ms trigger delay
 * - Auto-dismiss (default 4s)
 * - Hover pauses timer; resume continues remaining time (no restart)
 * - Dedup by actionKey: no duplicates; retrigger extends remaining time
 * - Duration extension capped at 8s total from first render
 * - Stack max 3 visible; 4th evicts oldest (with exit animation)
 */

const TRIGGER_DELAY_MS = 300;
const DEFAULT_DURATION_MS = 4000;
const MAX_TOTAL_VISIBLE_MS = 8000;
const MAX_VISIBLE = 3;

// Must match CSS exit animation duration in toast-item.component.css
const EXIT_ANIM_MS = 220;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts$ = new BehaviorSubject<Toast[]>([]);
  readonly toasts$ = this._toasts$.asObservable();

  private readonly dismissTimers = new Map<string, number>();
  private readonly pendingByAction = new Map<string, number>();

  /**
   * Trigger a toast.
   * If input.durationMs is omitted, DEFAULT_DURATION_MS is used.
   */
  trigger(input: ToastTrigger): void {
    const now = Date.now();

    // 1) Dedup: same actionKey already visible -> extend (no new toast, no re-enter animation)
    const existing = this._toasts$.value.find(
      (t) => t.actionKey === input.actionKey && !t.closing
    );
    if (existing) {
      this.extendDuration(existing.id, now);
      return;
    }

    // 2) Prevent duplicates during the 300ms delay window
    if (this.pendingByAction.has(input.actionKey)) return;

    // 3) Delay before rendering
    const handle = window.setTimeout(() => {
      this.pendingByAction.delete(input.actionKey);
      this.addToast(input);
    }, TRIGGER_DELAY_MS);

    this.pendingByAction.set(input.actionKey, handle);
  }

  /**
   * Request a dismiss that plays the exit animation, then removes the toast.
   */
  requestDismiss(id: string): void {
    const list = this._toasts$.value;
    const toast = list.find((t) => t.id === id);
    if (!toast || toast.closing) return;

    toast.closing = true;
    this.clearAutoDismiss(id);

    // IMPORTANT: emit state so UI applies the `.leaving` class
    this._toasts$.next([...list]);

    window.setTimeout(() => this.dismissNow(id), EXIT_ANIM_MS);
  }

  /**
   * Pause auto-dismiss timer on hover.
   * If the toast already expired, do NOT allow hover to "hold" it; dismiss instead.
   */
  pause(id: string): void {
    const toast = this._toasts$.value.find((t) => t.id === id);
    if (!toast || toast.paused || toast.closing) return;

    const now = Date.now();
    const remaining = toast.expiresAt - now;

    if (remaining <= 0) {
      // If already expired, continue dismissal animation (no hover hold).
      this.requestDismiss(id);
      return;
    }

    this.clearAutoDismiss(id);
    toast.paused = true;
    toast.remainingMs = remaining;

    // No _toasts$.next() here on purpose (hover should not trigger stack reflow animations).
  }

  /**
   * Resume auto-dismiss timer with remaining time (no restart).
   */
  resume(id: string): void {
    const toast = this._toasts$.value.find((t) => t.id === id);
    if (!toast || !toast.paused || toast.closing) return;

    const now = Date.now();
    toast.paused = false;
    toast.expiresAt = now + Math.max(0, toast.remainingMs);

    // No _toasts$.next() here on purpose.
    this.scheduleAutoDismiss(toast);
  }

  // ---------- Internals ----------

  private addToast(input: ToastTrigger): void {
    const now = Date.now();
    const durationMs = input.durationMs ?? DEFAULT_DURATION_MS;

    const toast: Toast = {
      id: this.newId(),
      actionKey: input.actionKey,
      message: input.message,
      variant: input.variant,
      durationMs,
      createdAt: now,
      expiresAt: now + durationMs,
      paused: false,
      remainingMs: durationMs,
      closing: false,
    };

    // Stack: newest on top
    let next = [toast, ...this._toasts$.value.filter((t) => !t.closing)];

    // Enforce max visible: if 4th arrives, dismiss the oldest (bottom)
    while (next.length > MAX_VISIBLE) {
      const oldest = next[next.length - 1];
      this.requestDismiss(oldest.id);
      next = next.slice(0, next.length - 1);
    }

    // IMPORTANT: emit state so UI renders the new toast(s)
    this._toasts$.next(next);

    this.scheduleAutoDismiss(toast);
  }

  private extendDuration(id: string, now = Date.now()): void {
    const toast = this._toasts$.value.find((t) => t.id === id);
    if (!toast || toast.closing) return;

    const capEnd = toast.createdAt + MAX_TOTAL_VISIBLE_MS;
    const base = toast.durationMs ?? DEFAULT_DURATION_MS;

    // Extend rule: try to keep visible for "base duration from now", capped by total max
    const desiredEnd = Math.min(capEnd, now + base);

    if (toast.paused) {
      toast.remainingMs = Math.max(toast.remainingMs, desiredEnd - now);
      toast.expiresAt = now + toast.remainingMs;
      return;
    }

    if (desiredEnd > toast.expiresAt) {
      toast.expiresAt = desiredEnd;
      this.clearAutoDismiss(id);
      this.scheduleAutoDismiss(toast);
    }
  }

  private scheduleAutoDismiss(toast: Toast): void {
    if (toast.paused || toast.closing) return;

    const remaining = Math.max(0, toast.expiresAt - Date.now());

    const handle = window.setTimeout(() => {
      this.requestDismiss(toast.id);
    }, remaining);

    this.dismissTimers.set(toast.id, handle);
  }

  private clearAutoDismiss(id: string): void {
    const handle = this.dismissTimers.get(id);
    if (handle != null) {
      window.clearTimeout(handle);
      this.dismissTimers.delete(id);
    }
  }

  private dismissNow(id: string): void {
    this.clearAutoDismiss(id);
    this._toasts$.next(this._toasts$.value.filter((t) => t.id !== id));
  }

  private newId(): string {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}
